import { Texture } from "pixi.js";

import { ensureFontReady } from "../../../../hooks/useGoogleFont";

/**
 * Rasterise an inline SVG string into a Pixi {@link Texture} via the browser's
 * native image decoder (`new Image()` → `Texture.from(img)`), mirroring
 * `OverlayImageRenderer` / `BackgroundImageRenderer`.
 *
 * We deliberately do NOT use Pixi's `Assets.load` / `loadSvg` parser: it keys
 * off the URL extension and skips `data:` URLs, and its own SVG rasteriser does
 * not see `document.fonts`, so Google-font text would fall back to a system
 * face. Loading the SVG through an `<img>` element makes the browser rasterise
 * it exactly like the DOM preview — including the `@font-face` faces that
 * `ensureFontReady` has already injected/decoded.
 *
 * `renderScale` controls the backing-store resolution: the SVG declares a card
 * viewBox (e.g. 600×300) but the widget can be drawn far larger on the 1920×
 * 1080 canvas, so we render the bitmap at `card × renderScale` and let the
 * caller scale the sprite to the widget bounds. This keeps the design's text
 * crisp without re-laying-out the SVG.
 */

/**
 * Encode an SVG markup string as a `data:image/svg+xml` URL.
 *
 * Uses `encodeURIComponent` (UTF-8 percent-encoding) rather than `btoa`: the
 * timer designs contain non-Latin-1 glyphs (Japanese katakana such as
 * "もうすぐ", the "●"/"▮" markers, "·" separators). `btoa` throws on those code
 * points; percent-encoding handles the full BMP and keeps the markup readable
 * in devtools.
 */
export function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Inject an explicit pixel `width`/`height` on the root `<svg>` so the browser
 * rasterises the data-URL `<img>` at a known backing-store size (an `<svg>`
 * with only a `viewBox` and no intrinsic size rasterises at a UA default of
 * 150×150 inside an `<img>`, which would look blurry when scaled up).
 *
 * The `viewBox` is preserved, so all card coordinates stay valid — we are only
 * choosing the output bitmap resolution.
 */
function withPixelSize(svg: string, w: number, h: number): string {
  const widthAttr = ` width="${w}"`;
  const heightAttr = ` height="${h}"`;
  // Match the opening <svg ...> tag and append explicit dimensions. If the SVG
  // already declared width/height (the catalog cards do not), strip them first
  // so ours win.
  return svg.replace(/<svg\b([^>]*)>/, (_m, attrs: string) => {
    const cleaned = attrs
      .replace(/\swidth="[^"]*"/, "")
      .replace(/\sheight="[^"]*"/, "");
    return `<svg${cleaned}${widthAttr}${heightAttr}>`;
  });
}

/**
 * Ensure every Google font a design references is loaded AND decoded before we
 * rasterise. The SVG-in-`<img>` rasteriser reads `document.fonts`, so a face
 * that isn't ready yet falls back to a system family and the raster shows the
 * wrong typeface. Resolves best-effort: a font that never loads simply doesn't
 * block the draw (the caller re-rasterises once `Promise.all` settles).
 *
 * `fontSize` is a representative size for `document.fonts.load` — the timer
 * designs use large display sizes, so we pass a big value to make sure the
 * heavy display weights are decoded, not just a small fallback metric.
 */
export async function ensureDesignFontsReady(
  fonts: readonly string[],
  fontSize = 120,
): Promise<void> {
  await Promise.all(
    fonts.map((f) =>
      // Designs use a range of weights (Anton 400, Orbitron 900, …). Load the
      // common heavy weights too so the rasteriser doesn't faux-bold/-thin.
      Promise.all([
        ensureFontReady(f, fontSize, 400),
        ensureFontReady(f, fontSize, 700),
      ]),
    ),
  ).then(() => undefined);
}

/**
 * Rasterise `svg` (a self-contained card SVG with a `viewBox`) into a
 * {@link Texture} sized `cardWidth*renderScale × cardHeight*renderScale`.
 * Resolves with the decoded texture or rejects if the browser fails to decode
 * the markup.
 */
export function rasterizeSvgToTexture(
  svg: string,
  cardWidth: number,
  cardHeight: number,
  renderScale = 1,
): Promise<Texture> {
  const w = Math.max(1, Math.round(cardWidth * renderScale));
  const h = Math.max(1, Math.round(cardHeight * renderScale));
  const sized = withPixelSize(svg, w, h);
  const url = svgToDataUrl(sized);

  return new Promise<Texture>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        resolve(Texture.from(img));
      } catch (err) {
        reject(err instanceof Error ? err : new Error("svg decode failed"));
      }
    };
    img.onerror = (err) =>
      reject(err instanceof Error ? err : new Error("svg image load failed"));
    img.src = url;
  });
}
